import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './utStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const UTCreate = () => {
    const currentDate = new Date().toISOString().slice(0, 16); // Format: 'YYYY-MM-DDTHH:mm'
    const [formValues, setFormValues] = useState({
        EmployeeID: '',
        DateStart: currentDate,
        DateEnd: currentDate,
        Remarks: '',
    });

    const [employeeList, setEmployeeList] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isDisable, setIsDisable] = useState(false);
    const navigate = useNavigate();
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    useEffect(() => {
        const fetchEmployeeData = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${apiUrl}/Employee/Namelist`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    setErrorMessage('Failed to fetch employee data');
                    setSuccessMessage('');
                    return;
                }

                const data = await response.json();
                setEmployeeList(data.Employee);

                if (data.Employee.length > 0) {
                    setFormValues({ ...formValues, EmployeeID: data.Employee[0].EmployeeID });
                }

            } catch (error) {
                setErrorMessage('Error fetching employee data');
                setSuccessMessage('');
            }
        };

        fetchEmployeeData();
    }, []);

    const handleInputChange = (e) => {
        setFormValues({ ...formValues, [e.target.name]: e.target.value });
    };

    const calculateDuration = () => {
        const startDate = new Date(formValues.DateStart);
        const endDate = new Date(formValues.DateEnd);
        const durationInMinutes = (endDate - startDate) / (1000 * 60);

        // Convert duration to hours and minutes in decimal format
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        const durationInDecimal = hours + minutes / 60;

        return isNaN(durationInDecimal) ? 0 : durationInDecimal.toFixed(2);
    };

    const handleForSubmit = async (e) => {
        e.preventDefault();

        const { EmployeeID, DateStart, DateEnd, Remarks } = formValues;

        const duration = calculateDuration(DateStart, DateEnd);
        if (duration <= 0) {
            setErrorMessage('DateStart must be earlier than DateEnd');
            setSuccessMessage('');
            return;
        }

        // Disable the button when the API process starts
        setIsDisable(true);

        // Create New Undertime Object
        const newUndertime = {
            EmployeeID,
            DateStart,
            DateEnd,
            Remarks,
        };

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${apiUrl}/Undertime/Create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newUndertime),
            });

            if (!response.ok) {
                const errorData = await response.json();
                setErrorMessage(errorData.Message);
                setSuccessMessage('');
                return;
            }
            const data = await response.json();

            setErrorMessage('');
            setSuccessMessage(data.Message);

            setIsDisable(false);
            // Use react-router-dom's useNavigate hook for navigation
            navigate('/undertime-list');
        } catch (error) {
            setErrorMessage('Error creating undertime');
            setSuccessMessage('');
            setIsDisable(false);
        }
    };

    return (
        <div>
            {(userAccess.hasAdmin || userAccess.hasStandard) ? (
                <>
                    <h3>Create Undertime</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    <form onSubmit={handleForSubmit}>
                        <div className='mb-3'>
                            <label htmlFor='employeeID' className='form-label'>Employee</label>
                            <select
                                type="text"
                                className="form-control custom-width"
                                id="employeeID"
                                name="EmployeeID"
                                value={formValues.EmployeeID}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">---Select an employee---</option>
                                {employeeList.map(employee => (
                                    <option
                                        key={employee.EmployeeID}
                                        value={employee.EmployeeID}>
                                        {`${employee.Name}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='dateStart' className='form-label'>Date Start</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="dateStart"
                                name="DateStart"
                                value={formValues.DateStart}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='dateEnd' className='form-label'>Date End</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="dateEnd"
                                name="DateEnd"
                                value={formValues.DateEnd}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='duration' className='form-label'>Duration (hours)</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="duration"
                                name="duration"
                                value={calculateDuration()}
                                readOnly={true}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="remarks" className="form-label">Remarks</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="remarks"
                                name="Remarks"
                                value={formValues.Remarks}
                                onChange={handleInputChange}
                                maxLength={500}
                                required
                            />
                        </div>
                        <Link to="/undertime-list" className="btn btn-secondary me-2">Back</Link>
                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isDisable}
                            >
                                {isDisable ? 'Creating...' : 'Save'}
                            </button>
                        )}
                    </form>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    );
};

export default UTCreate;